import {
  GoalOverviewRoadmapViewOptions,
  TimelineTimeframes,
} from '@griply/types';
import {
  buildShowCompletedGoalsUpdate,
  getShowCompletedGoals,
  GoalCompletedContext,
} from '@griply/utils';
import { useGoalById } from 'features/goals';
import { useUpdateUserSettings } from 'features/user/hooks/use-update-user-settings';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  Plus,
  SettingsSort18,
  Table14,
  Timeline214,
} from 'shared/assets/icons';
import { TimelinePremiumLock } from 'shared/components/connected/timeline-premium-lock';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { Option, SliderButtons } from 'shared/components/ui/slider-buttons';
import { TimeframeNavigationButtons } from 'shared/components/ui/timeframe-navigation-buttons';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useUser } from 'shared/hooks/use-user';
import { LimitKeys } from 'shared/types/limit-keys';
import { useTheme } from 'styled-components';

import { GoalDetailRoadmapAddMenu } from './components/goal-detail-roadmap-add-menu';
import { RoadmapFilterButton } from './components/roadmap-filter-button';
import { RoadmapTimeline } from './components/roadmap-timeline';
import { SubgoalsOverview } from './components/subgoals-overview';
import * as Styled from './goal-detail.style';
import { useGoalRoadmapControls } from './hooks/use-goal-roadmap-controls';

export const Roadmap: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const theme = useTheme();
  const goal = useGoalById(id);
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const interactionsRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

  const {
    view,
    setView,
    timeframe,
    setTimeframe,
    referenceDate,
    scrollRequest,
    onPrev,
    onToday,
    onNext,
  } = useGoalRoadmapControls(goal);
  const { allowed: isTimelineAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopGoalsOverviewTimeline,
    {},
  );
  const isTimeline =
    !theme.isMobile && view === GoalOverviewRoadmapViewOptions.Timeline;
  const showTimeline = isTimeline && isTimelineAllowed;

  const viewOptions = useMemo<Option<GoalOverviewRoadmapViewOptions>[][]>(
    () => [
      [
        {
          icon: Table14,
          value: GoalOverviewRoadmapViewOptions.List,
        },
        {
          icon: Timeline214,
          value: GoalOverviewRoadmapViewOptions.Timeline,
        },
      ],
    ],
    [],
  );

  const timeframeOptions = useMemo<Option<TimelineTimeframes>[][]>(
    () => [
      [
        {
          label: t('general.labels.timeframes.month.short'),
          value: TimelineTimeframes.Month,
        },
        {
          label: t('general.labels.timeframes.quarter.short'),
          value: TimelineTimeframes.Quarter,
        },
        {
          label: t('general.labels.timeframes.year.short'),
          value: TimelineTimeframes.Year,
        },
        {
          label: t('general.labels.timeframes.five-years.short'),
          value: TimelineTimeframes.FiveYears,
        },
      ],
    ],
    [t],
  );

  const showCompletedGoals = getShowCompletedGoals(
    user?.settings,
    GoalCompletedContext.GoalDetail,
  );

  const onShowCompletedGoals = (value: boolean) => {
    updateUserSettings(
      buildShowCompletedGoalsUpdate(GoalCompletedContext.GoalDetail, value),
    );
  };

  const handleOpenAddMenu = () => setIsAddMenuOpen(true);
  const handleCloseAddMenu = () => setIsAddMenuOpen(false);

  if (!goal) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.goal-detail.tabs.roadmap.label')}
      fullSize
      noPadding
      centerInteractions={
        theme.isMobile ? undefined : (
          <>
            <SliderButtons
              optionGroups={viewOptions}
              onOption={setView}
              value={view}
              preventDrag
            />
            {showTimeline && (
              <>
                <SliderButtons
                  optionGroups={timeframeOptions}
                  onOption={setTimeframe}
                  value={timeframe}
                  preventDrag
                />
                <TimeframeNavigationButtons
                  onPrev={onPrev}
                  onToday={onToday}
                  onNext={onNext}
                />
              </>
            )}
          </>
        )
      }
      interactions={
        <Styled.InteractionsContainer ref={interactionsRef}>
          <IconButton
            icon={Plus}
            ref={addButtonRef}
            onClick={handleOpenAddMenu}
            color={Colors.Default}
          />
          {isAddMenuOpen && (
            <PopupMenu
              referenceElement={addButtonRef}
              onOutside={handleCloseAddMenu}
              position="bottom-end"
            >
              <GoalDetailRoadmapAddMenu
                parentIds={[goal.id]}
                onClose={handleCloseAddMenu}
              />
            </PopupMenu>
          )}
          <RoadmapFilterButton goal={goal} />
          <IconButton
            icon={SettingsSort18}
            onClick={() => setIsSortMenuOpen(true)}
            color={Colors.Default}
          />
          {isSortMenuOpen && (
            <PopupMenu
              referenceElement={interactionsRef}
              onOutside={() => setIsSortMenuOpen(false)}
              position="bottom-end"
            >
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.goals.show-completed-goals')}
                    checked={showCompletedGoals}
                    onChange={onShowCompletedGoals}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.InteractionsContainer>
      }
    >
      {!isTimeline && (
        <SubgoalsOverview
          goal={goal}
          hideCompleted={!showCompletedGoals}
          fullSize
        />
      )}
      {isTimeline &&
        (isTimelineAllowed ? (
          <RoadmapTimeline
            goal={goal}
            timeframe={timeframe}
            referenceDate={referenceDate}
            scrollRequest={scrollRequest}
            hideCompleted={!showCompletedGoals}
          />
        ) : (
          <TimelinePremiumLock
            title={t('pages.goal-detail.tabs.roadmap.label')}
          />
        ))}
    </PageSectionBase>
  );
};
